import { deviceType as dType, primaryInput } from 'detect-it';
import { ImageTransform } from '../../../../app/package/base/cms/package/cms-domain';

export const DevicePointerType = {
  MOUSE: 'mouse',
  TOUCH: 'touch',
} as const;

export type DevicePointerType = (typeof DevicePointerType)[keyof typeof DevicePointerType];

export const LayoutType = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
} as const;

export type LayoutType = (typeof LayoutType)[keyof typeof LayoutType];

const _DetectedDeviceType = {
  HYBRID: 'hybrid',
  MOUSE_ONLY: 'mouseOnly',
  TOUCH_ONLY: 'touchOnly',
} as const;

type _DetectedDeviceType = (typeof _DetectedDeviceType)[keyof typeof _DetectedDeviceType];

export function devicePointerType(): DevicePointerType {
  return _memoizedDeviceType();
}

export function layoutType() {
  if (isMobileWidth()) {
    return LayoutType.MOBILE;
  }
  if (isTabletWidth()) {
    return LayoutType.TABLET;
  }
  return LayoutType.DESKTOP;
}

export function isMobileWidth() {
  return window.matchMedia('(max-width: 47.9375rem)').matches; // 767px
}

export function isTabletWidth() {
  return window.matchMedia('(min-width: 48rem) and (max-width: 63.9375rem)').matches; // 768px and 1023px
}

export function isDesktopWidth() {
  return window.matchMedia('(min-width: 64rem)').matches; // 1024px
}

export function isPortrait() {
  return window.matchMedia('(orientation: portrait)').matches;
}

export function isLandscape() {
  return window.matchMedia('(orientation: landscape)').matches;
}

export function getDeviceOrientation(): 'portrait' | 'landscape' {
  const orientation = screen.orientation;

  if (orientation?.type) {
    if (orientation.type.includes('portrait')) {
      return 'portrait';
    }
    if (orientation.type.includes('landscape')) {
      return 'landscape';
    }
  }

  // Fallback for older browsers or environments without screen.orientation
  const portraitMatch = window.matchMedia('(orientation: portrait)').matches;
  return portraitMatch ? 'portrait' : 'landscape';
}

function _handleHybridTypes(type: _DetectedDeviceType): DevicePointerType {
  if (type === _DetectedDeviceType.HYBRID) {
    if (primaryInput === 'mouse') {
      window.$app.logger.log(
        `[device] set hybrid device type to: '${DevicePointerType.MOUSE}', based on primaryInput '${primaryInput}'`,
      );
      return DevicePointerType.MOUSE;
    }
    window.$app.logger.log(
      `[device] set hybrid device type to: '${DevicePointerType.TOUCH}', based on primaryInput '${primaryInput}'`,
    );
    return DevicePointerType.TOUCH;
  }

  if (type === _DetectedDeviceType.MOUSE_ONLY) {
    window.$app.logger.log(
      `[device] set device type to: '${DevicePointerType.MOUSE}', based on type '${_DetectedDeviceType.MOUSE_ONLY}'`,
    );
    return DevicePointerType.MOUSE;
  }

  window.$app.logger.log(
    `[device] set device type to: '${DevicePointerType.TOUCH}', based on type '${_DetectedDeviceType.TOUCH_ONLY}'`,
  );
  return DevicePointerType.TOUCH;
}

const _memoizedDeviceType: () => DevicePointerType = (() => {
  let deviceType: DevicePointerType | undefined;
  return () => {
    if (deviceType === undefined) {
      deviceType = _handleHybridTypes(dType as _DetectedDeviceType);
    }
    return deviceType;
  };
})();

export function getImageTransformValueForDevice(): string {
  if ('connection' in navigator) {
    const connection = navigator.connection as {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
    window.$app.logger.log(`connection: Effective Type: ${connection.effectiveType}`);
    window.$app.logger.log(`connection: Downlink (Mbps): ${connection.downlink}`);
    window.$app.logger.log(`connection: RTT (ms): ${connection.rtt}`);
    const slowEffectiveTypes = ['slow-2g', '2g', '3g']; // epxected values: 'slow-2g', '2g', '3g', '4g'
    if (slowEffectiveTypes.includes(connection.effectiveType as string)) {
      window.$app.logger.log(`connection: Set image transform to ${ImageTransform.LOW}`);
      return ImageTransform.LOW;
    }
    window.$app.logger.log(`connection: Set image transform to ${ImageTransform.MID}`);
    return ImageTransform.MID;
  }
  window.$app.logger.log('connection: No connection information available');
  return ImageTransform.MID;
}
